"use strict";
// Copyright 2022 Fergal Daly <fergald@gmail.com>.
Object.defineProperty(exports, "__esModule", { value: true });
exports.TcbCalendar = exports.Presenter = exports.DaySplit = exports.CalendarLinguist = void 0;
const event_api = require("./api/event");
const calendar_api = require("./calendar_api");
const dates = require("./dates");
const h = require("./html");
const lingual = require("./lingual");
const pad = require("./pad");
const SiteRevision = require("./site_revision");
const CALENDAR_TEMPLATE = `
<style>
  .day {
    width: 14%;
    padding: 0;
    vertical-align: top;
  }

  .header, .footer {
    position: sticky;
    opacity: 100%;
    background-color: var(--background-color);
    z-index: 1;
  }
  .header {
    top: 0px;
  }
  .footer {
    bottom: 0px;
  }

  .header .day {
    text-align: center;
  }

  .week {
    display: flex;
  }

  .event {
    margin: 2px;
  }

  .event .title {
    font-weight: bold;
  }

  .event .description {
    display: none;
  }

  .ticket-container {
    text-align:center;
    background-color: white;
    display: block;
    opacity: 100%;
    border: solid 1px black;
  }

  .info-link {
    text-decoration: underline;
  }

  .show.en {
    background-color: #00A2C3;
  }

  .show.ja {
    background-color: #E41055;
  }

  .workshop {
    background-color: #BA8100;
  }

  .day-title {
    padding-right: 1ch;
  }

  .day, .event  {
    word-wrap: break-word;
  }

  .month-name {
    text-align: center;
    width: 100%;
  }

  #calendar {
    width: var(--customElementWidth);
    background-color: var(--background-color);
    text-align: center;
  }

  .filters {
    font-size: 75%;
    width: 100%;
  }

  .page-controls {
    width: 100%;
    text-align: center;
  }
  .previous-page {
    position: absolute;
    left:0;
  }
  .next-page {
    position: absolute;
    right:0;
  }

  ::slotted(a.link-button) {
    color: inherit;
  }

  a.link-button {
    color: inherit;
    text-decoration: inherit;
  }

  @keyframes pulse {
    0% {
     transform: scale(1, 1);
    }

    50% {
     transform: scale(0.3, 0.3);
    }

    100% {
    transform: scale(1, 1);
    }
  }
</style>

<style id="filterStyle"></style>
<style id="style-sheet"></style>
<style id="mode-style-sheet"></style>

<div id="calendar">
  <div>
    <div class="header">
      <div class="page-controls">
        <a href="#" aria-label="previous" class="previous-page link-button">&lt;&lt;&lt;</a>
        <a href="#" aria-label="next" class="next-page link-button">&gt;&gt;&gt;</a>
        <span>&nbsp;</span>
      </div>
      <div class="filters">
        <span x-msg=filter>Filter</span>:
        <a href="#" id="filter-" class="filter link-button" x-msg=filter-all>All</a> /
        <a href="#" id="filter-show-en" class="filter link-button" x-msg=filter-show-en>EN🇬🇧</a> /
        <a href="#" id="filter-show-ja" class="filter link-button" x-msg=filter-show-ja>JA🇯🇵</a> /
        <a href="#" id="filter-workshop" class="filter link-button" x-msg=filter-workshops>Workshops</a>
      </div>
      <div class="week">
        <div id=day-1 class="day" x-msg=day-1>
          Monday
        </div>
        <div id=day-2 class="day" x-msg=day-2>
          Tuesday
        </div>
        <div id=day-3 class="day" x-msg=day-3>
          Wednesday
        </div>
        <div id=day-4 class="day" x-msg=day-4>
          Thursday
        </div>
        <div id=day-5 class="day" x-msg=day-5>
          Friday
        </div>
        <div id=day-6 class="day" x-msg=day-6>
          Saturday
        </div>
        <div id=day-7 class="day" x-msg=day-7>
          Sunday
        </div>
      </div>
    </div>
    <div id="body" class="body">
    </div>
    <div class="footer">
      <div class="page-controls">
        <a href="#" aria-label="previous" class="previous-page link-button">&lt;&lt;&lt;</a>
        <a href="#" aria-label="next" class="next-page link-button">&gt;&gt;&gt;</a>
        <span>&nbsp;</span>
      </div>
    </div>
  </div>
</div>
`;
const GRID_STYLE_SHEET = `
.day-name {
  display: none;
}
`;
const LINEAR_STYLE_SHEET = `
.header .week {
  display: none;
}
.day-name {
  display: initial;
}
.day {
  width: inherit;
}
`;
const FIXED_STYLE_SHEET = LINEAR_STYLE_SHEET +
    `
.page-controls {
  display: none;
}
`;
const LINEAR_MODES = [
    calendar_api.Modes.linear,
    calendar_api.Modes.fixed,
];
const MESSAGES = lingual.createLanguageMap([lingual.EN, lingual.JA], [
    ['filter', 'Filter', 'タイプ'],
    ['filter-all', 'All', 'すべて'],
    ['filter-show-en', 'EN🇬🇧', '英🇬🇧'],
    ['filter-show-ja', 'JA🇯🇵', '日🇯🇵'],
    ['filter-workshops', 'Workshops', 'ワークショップ'],
    ['today', 'Today', '今日'],
    ['info', 'info', 'info'],
    ['buy-tickets', 'Buy Tickets', 'チケット購入'],
    ['previous', 'previous', '前'],
    ['next', 'next', '次'],
]);
class CalendarLinguist extends lingual.Linguist {
    constructor(linguist, messages) {
        super(linguist.language, messages);
        this.linguist = linguist;
    }
    static fromLanguage(language) {
        const parentLinguist = lingual.Linguist.fromLanguage(language);
        return new CalendarLinguist(parentLinguist, MESSAGES.getMessageMapOrThrow(language));
    }
    static fromDocument() {
        return this.fromLanguage(lingual.languageFromDocument(document));
    }
    yearAndMonthForHeader(year, month) {
        const month_s = this.linguist.getMonth(month);
        switch (this.language) {
            case lingual.JA:
                return `${year}年 ${month_s}`;
            default:
                return `${year}-${pad.n2(month)} (${month_s})`;
        }
    }
}
exports.CalendarLinguist = CalendarLinguist;
var DaySplit;
(function (DaySplit) {
    DaySplit[DaySplit["Before"] = 1] = "Before";
    DaySplit[DaySplit["Same"] = 2] = "Same";
    DaySplit[DaySplit["After"] = 3] = "After";
})(DaySplit || (exports.DaySplit = DaySplit = {}));
class Now {
    constructor(timezone, nowMs) {
        this.ms = nowMs;
        this.today = timezone.getSimpleDateFromMs(nowMs).getDayStart();
        this.tomorrow = this.today.addDays(1);
    }
}
class Presenter {
    constructor(timezone, nowMs, linguist) {
        this.timezone = timezone;
        this.timeInfo = new Now(this.timezone, nowMs);
        this.linguist = linguist;
    }
    setNowMs(nowMs) {
        this.timeInfo = new Now(this.timezone, nowMs);
    }
    getDaySplit(date) {
        if (dates.SimpleDate.compare(date, this.timeInfo.today) == -1) {
            return DaySplit.Before;
        }
        if (dates.SimpleDate.compare(date, this.timeInfo.tomorrow) == -1) {
            return DaySplit.Same;
        }
        return DaySplit.After;
    }
    getDaySplitClass(daySplit) {
        switch (daySplit) {
            case DaySplit.Before:
                return 'past';
            case DaySplit.Same:
                return 'today';
            case DaySplit.After:
                return 'future';
        }
    }
    getDayDate(day, linear) {
        const withMonth = `${pad.n2(day.getMonth())}-${pad.n2(day.getDate())}`;
        if (day.getDate() == 1) {
            if (day.getMonth() == 1) {
                return day.toString();
            }
            return withMonth;
        }
        if (linear || day.isWeekStart()) {
            return withMonth;
        }
        return `${day.getDate()}`;
    }
    getTimeSplit(ms) {
        return ms < this.timeInfo.ms ? 'past' : 'future';
    }
    getTimeSplitFromSimpleDate(date) {
        return this.getTimeSplit(this.timezone.getMsFromSimpleDate(date));
    }
    getTimeSplitFromEvent(event) {
        const duration = event.durationMinutes ? event.durationMinutes : 60;
        return this.getTimeSplit(event.time + (event.durationMinutes || 0) * dates.ONE_MINUTE_MS);
    }
    getTicketInfoFromEvent(event) {
        if (!event.ticketUrl) {
            return null;
        }
        return [event.ticketUrl, this.linguist.getMessage('buy-tickets')];
    }
    getTicketInfoFromEventWithPrice(event) {
        if (event.ticketUrl) {
            return [
                event.ticketUrl,
                event.ticketPrice
                    ? `${event.ticketPrice}￥`
                    : this.linguist.getMessage('buy-tickets'),
            ];
        }
        else if (event.ticketPrice) {
            return [null, `${event.ticketPrice}￥`];
        }
        return [null, null];
    }
    getRangeData(year, month, day, weeks) {
        if (year == null) {
            console.log('year is undefined');
        }
        if (month == null) {
            console.log('month is undefined');
        }
        if (day == null) {
            console.log('day is undefined');
            day = 1;
        }
        if (weeks == null) {
            console.log('weeks is undefined');
            weeks = 5;
        }
        const begin = this.timezone.getSimpleDateFromParts(year, month, day);
        const end = begin.addWeeks(weeks);
        return {
            begin: begin,
            end: end,
            previous: begin.addWeeks(-weeks),
            next: end,
            weeks: weeks,
        };
    }
    getRangeDataFromData(data) {
        return this.getRangeData(data.year, data.month, data.day, data.weeks);
    }
    // return a <= d < b
    getDayLimits(year, month, day, weeks) {
        if (year == null) {
            console.log('year is undefined');
        }
        if (month == null) {
            console.log('month is undefined');
        }
        if (weeks == null && day == null) {
            return dates.limitsOfMonth(year, month);
        }
        else {
            if (weeks == null) {
                console.log('weeks is undefined');
                weeks = 5;
            }
            if (day == null) {
                console.log('day is undefined');
                day = 1;
            }
            let start = this.timezone.getSimpleDateFromParts(year, month, day);
            if (!start.isWeekStart()) {
                console.log("Doesn't start on a week-start", start.utcDate);
                start = dates.getWeekStart(start);
            }
            return [start, start.addWeeks(weeks)];
        }
    }
    // return a <= d < b
    getDayLimitsFromData(data) {
        return this.getDayLimits(data.year, data.month, data.day, data.weeks);
    }
}
exports.Presenter = Presenter;
class EventQuery {
    constructor(eventsQueryUrl, begin, end, newDataCallback) {
        this.timezone = new dates.Timezone(9); // Japan time.
        this.eventsByDate = new event_api.EventsByDate();
        this.cancelled = false;
        this.eventsQueryUrl = eventsQueryUrl;
        this.begin = begin;
        this.end = end;
        this.newDataCallback = newDataCallback;
    }
    cancel() {
        this.cancelled = true;
    }
    fetchAndUpdate(begin) {
        const beginMs = this.timezone.getMsFromSimpleDate(begin || this.begin);
        const endMs = this.timezone.getMsFromSimpleDate(this.end);
        return new Promise(resolve => {
            calendar_api
                .fetchEvents(this.eventsQueryUrl, beginMs, endMs)
                .then(results => {
                if (this.cancelled) {
                    resolve(false);
                }
                const lastTimestamp = this.updateEvents(results.events);
                if (lastTimestamp) {
                    this.fetchAndUpdate((begin = lastTimestamp));
                }
                resolve(true);
            });
        });
    }
    updateEvents(eventJsons) {
        let lastTimestamp;
        const events = event_api.upgradeEvents(this.timezone, eventJsons);
        for (const event of events) {
            // Ignore events outside the time range.
            if (dates.SimpleDate.compare(event.simpleDate, this.begin) < 0 ||
                dates.SimpleDate.compare(event.simpleDate, this.end) > 0) {
                continue;
            }
            if (!this.eventsByDate.has(event)) {
                if (lastTimestamp === undefined) {
                    lastTimestamp = event.simpleDate;
                }
                else if (dates.SimpleDate.compare(lastTimestamp, event.simpleDate) == -1) {
                    lastTimestamp = event.simpleDate;
                }
                this.eventsByDate.add(event);
            }
        }
        if (lastTimestamp) {
            this.newDataCallback(this.eventsByDate);
        }
        return lastTimestamp;
    }
}
class TcbCalendar extends HTMLElement {
    constructor() {
        super();
        this.timezone = new dates.Timezone(9); // Japan time.
        this.linear = false;
        this.fixed = false;
        this.fetching = false;
        this.eventsByDate = new event_api.EventsByDate();
        this.slotCounter = 0;
        this.linguist = CalendarLinguist.fromDocument();
        this.presenter = new Presenter(this.timezone, new Date().getTime(), this.linguist);
        this.root = this.attachShadow({ mode: 'open' });
        this.root.innerHTML = CALENDAR_TEMPLATE;
        this.validFilters = this.setupFilters(this.root);
    }
    connectedCallback() {
        this.translateMessages(this.root);
    }
    translateMessages(element) {
        const elements = this.root.querySelectorAll('[x-msg]');
        elements.forEach((element) => {
            const msgId = element.getAttribute('x-msg');
            if (msgId !== null) {
                element.textContent = this.linguist.getMessage(msgId);
            }
        });
    }
    getFilterElements() {
        return this.root.querySelectorAll('.filter');
    }
    setupFilters(element) {
        const validFilters = [];
        this.getFilterElements().forEach((filter) => {
            const filterId = filter.id.replace('filter-', '');
            validFilters.push(filterId);
            filter.onclick = () => {
                this.setAttribute('filter', filterId);
            };
        });
        return validFilters;
    }
    static get observedAttributes() {
        return ['data', 'filter', 'mode', 'show-filters', 'style-sheet'];
    }
    attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'data') {
            this.updateData(oldValue, newValue);
        }
        else if (name === 'style-sheet') {
            this.updateStyleSheet(oldValue, newValue);
        }
        else if (name == 'filter') {
            this.setFilter(newValue);
        }
        else if (name == 'show-filters') {
            this.setShowFilters(newValue);
        }
        else if (name == 'mode') {
            if (oldValue != newValue) {
                this.updateMode(newValue);
                this.forceUpdate();
            }
        }
    }
    getModeStyleSheet(mode) {
        switch (mode) {
            case 'linear':
                return LINEAR_STYLE_SHEET;
            case 'fixed':
                return FIXED_STYLE_SHEET;
            default:
                return GRID_STYLE_SHEET;
        }
    }
    updateMode(newMode) {
        this.linear = LINEAR_MODES.includes(newMode);
        this.fixed = newMode == calendar_api.Modes.fixed;
        if (this.fixed) {
            if (this.query) {
                this.query.cancel();
            }
        }
        const modeStyle = (this.root.getElementById('mode-style-sheet'));
        if (modeStyle) {
            modeStyle.textContent = this.getModeStyleSheet(newMode);
        }
    }
    forceUpdate() {
        const data = this.getAttribute('data');
        if (data) {
            this.updateData(null, data);
        }
    }
    updateQuery(query) {
        if (this.query) {
            this.query.cancel();
        }
        this.query = query;
    }
    newQuery(data) {
        const rangeData = this.presenter.getRangeDataFromData(data);
        const query = new EventQuery(data.eventsQueryUrl || '', rangeData.begin, rangeData.end, (eventsByDate) => {
            this.eventsByDate = eventsByDate;
            this.renderCalendar(data);
        });
        this.updateQuery(query);
        return query;
    }
    updateData(oldValue, newValue) {
        if (oldValue == newValue) {
            return;
        }
        const data = JSON.parse(newValue);
        if (data.eventsQueryUrl === null) {
            console.error("data doesn't contain events or a URL to get them from.");
            return;
        }
        const rangeData = this.presenter.getRangeDataFromData(data);
        const query = this.newQuery(data);
        const lastTimestamp = oldValue
            ? undefined
            : this.setInitialData(query, data);
        // In fixed mode, we just display the data we are given.
        if (!this.fixed) {
            query.fetchAndUpdate(lastTimestamp);
        }
    }
    setInitialData(query, data) {
        if (!data.events) {
            return undefined;
        }
        if (data.events.length > 0) {
            return query.updateEvents(data.events);
        }
    }
    updateStyleSheet(oldValue, newValue) {
        const filterStyle = (this.root.getElementById('style-sheet'));
        if (filterStyle) {
            filterStyle.textContent = newValue;
        }
    }
    setupHeaderFooter(data, rangeData) {
        function goTo(calendar, pageElement, day) {
            if (calendar.fetching)
                return;
            calendar.fetching = true;
            const newData = {
                year: day.getYear(),
                month: day.getMonth(),
                day: day.getDate(),
                now: data.now,
                weeks: data.weeks,
                events: [],
                eventsQueryUrl: data.eventsQueryUrl,
            };
            const query = calendar.newQuery(newData);
            const style = h.element('style', pageElement);
            style.textContent = `.${pageElement.classList[0]} {
        animation: pulse 3s linear infinite;
      }`;
            query.fetchAndUpdate().finally(() => {
                calendar.fetching = false;
                style.remove();
            });
        }
        const previousPageElements = this.root.querySelectorAll('.previous-page');
        for (const previousPageElement of previousPageElements) {
            previousPageElement.hidden = !data.eventsQueryUrl;
            previousPageElement.setAttribute('aria-label', this.linguist.getMessage('previous'));
            const previousMonth = dates.previousMonth(data.year, data.month);
            previousPageElement.onclick = () => goTo(this, previousPageElement, rangeData.previous);
        }
        if (previousPageElements.length == 0) {
            console.log('missing previous-page div');
        }
        const nextPageElements = this.root.querySelectorAll('.next-page');
        for (const nextPageElement of nextPageElements) {
            nextPageElement.hidden = !data.eventsQueryUrl;
            nextPageElement.setAttribute('aria-label', this.linguist.getMessage('next'));
            const nextMonth = dates.nextMonth(data.year, data.month);
            nextPageElement.onclick = () => goTo(this, nextPageElement, rangeData.next);
        }
        if (nextPageElements.length == 0) {
            console.log('missing next-page div');
        }
    }
    renderCalendar(data) {
        this.presenter.setNowMs(data.now);
        const bodyDiv = this.root.getElementById('body');
        if (!bodyDiv) {
            console.log('missing body div');
            return undefined;
        }
        bodyDiv.innerHTML = '';
        const rangeData = this.presenter.getRangeDataFromData(data);
        this.setupHeaderFooter(data, rangeData);
        if (this.linear) {
            const days = dates.daysOf(rangeData.begin, rangeData.end);
            this.addDays(bodyDiv, days);
        }
        else {
            let begin = rangeData.begin;
            if (!begin.isWeekStart()) {
                console.log("Doesn't start on a week-start", begin.utcDate);
                begin = dates.getWeekStart(begin);
            }
            const weeks = dates.weeksOf(dates.daysOf(begin, rangeData.end));
            for (const week of weeks) {
                const weekDiv = h.div(bodyDiv, 'week');
                this.addDays(weekDiv, week);
            }
        }
    }
    addDays(parent, days) {
        for (const day of days) {
            const events = this.eventsByDate.get(day) || new event_api.EventSet();
            if (this.fixed) {
                if (events.size() == 0) {
                    continue;
                }
            }
            this.dayDiv(parent, day, events);
        }
    }
    dayDiv(parentDiv, day, eventSet) {
        const daySplit = this.presenter.getDaySplit(day);
        const dayDiv = h.div(parentDiv, `day ${this.presenter.getDaySplitClass(daySplit)}`);
        const dayTitleDiv = h.div(dayDiv, 'day-title');
        let dayNameSpan, dayDateSpan;
        switch (this.linguist.language) {
            case lingual.JA:
                dayDateSpan = h.span(dayTitleDiv, 'day-date');
                h.span(dayTitleDiv).innerText = ' ';
                dayNameSpan = h.span(dayTitleDiv, 'day-name');
                break;
            default:
                dayNameSpan = h.span(dayTitleDiv, 'day-name');
                h.span(dayTitleDiv).innerText = ' ';
                dayDateSpan = h.span(dayTitleDiv, 'day-date');
                break;
        }
        dayNameSpan.innerText = this.linguist.linguist.getLongDay(day.getDayOfWeekNumber());
        dayDateSpan.innerText = this.presenter.getDayDate(day, this.linear);
        const dayEventsDiv = h.div(dayDiv, 'day-events');
        const events = Array.from(eventSet.values());
        event_api.sortByDate(events);
        for (const event of events) {
            this.eventDiv(dayEventsDiv, event, daySplit);
        }
    }
    eventDiv(parentDiv, event, daySplit) {
        // If the day is already before today, no need to mark this event in the past.
        const timeSplit = daySplit == DaySplit.Before
            ? ''
            : this.presenter.getTimeSplitFromEvent(event);
        const eventDiv = h.div(parentDiv, `event ${event.type} ${event.language} ${timeSplit}`);
        const linkableDiv = event.url
            ? h.a(eventDiv, SiteRevision.insertSiteRevision(new URL(document.location.href), event.url), 'event-link link-button')
            : h.div(eventDiv);
        const timeDiv = h.div(linkableDiv, 'time');
        timeDiv.innerText = event.simpleDate.getTime();
        const titleDiv = h.div(linkableDiv, 'title');
        titleDiv.innerText = event.title || '';
        const descriptionDiv = h.div(linkableDiv, 'description');
        descriptionDiv.innerHTML = event.description || '';
        const infoDiv = h.div(linkableDiv, 'info-link');
        infoDiv.innerText = this.linguist.getMessage('info');
        const ticketInfo = this.presenter.getTicketInfoFromEvent(event);
        if (ticketInfo) {
            const ticketContainer = h.div(eventDiv, 'ticket-container');
            const [ticketUrl, ticketText] = ticketInfo;
            let ticketParent = ticketContainer;
            const ticketLink = h.a(this, ticketUrl, 'link-button');
            // This is a hack to make the <a> appear outside the shadow root so that
            // Google Tag Manager can see it in events.
            const ticketSlot = h.slot(ticketContainer);
            ticketSlot.name = `${this.slotCounter++}`;
            ticketLink.slot = ticketSlot.name;
            ticketLink.setAttribute('target', '_blank');
            ticketParent = ticketLink;
            const ticketButton = h.div(ticketParent, 'ticket-button');
            ticketButton.innerText = ticketText;
        }
    }
    setFilter(filter) {
        if (this.validFilters.indexOf(filter) == -1) {
            filter = this.validFilters[0];
        }
        filter || (filter = '');
        const filterStyle = (this.root.getElementById('filterStyle'));
        const filterId = `filter-${filter}`;
        if (filterStyle) {
            const targetClass = filter ? '.' + filter.replace(/-/g, '.') : '';
            filterStyle.textContent = `
    .event { display: none; }
    .event${targetClass} { display: block }
    `;
        }
        else {
            console.log('no filter-style');
        }
        this.getFilterElements().forEach((filter) => {
            if (filter.id == filterId) {
                filter.classList.add('active-filter');
            }
            else {
                filter.classList.remove('active-filter');
            }
        });
    }
    setShowFilters(value) {
        const filters = this.root.querySelectorAll('.filters');
        const display = value == 'no' ? 'none' : 'unset';
        for (const filter of filters) {
            filter.style.display = display;
        }
    }
}
exports.TcbCalendar = TcbCalendar;
window.customElements.define('tcb-calendar', TcbCalendar);
